import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import Avatar from '../../components/Avatar';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { listStaff, inviteStaff, removeStaff } from '../../api/admin';
import InviteStaffDrawer from './InviteStaffDrawer';
import styles from './Admin.module.css';

const GROUP_TINT = {
  Admin: '#F2B922', Ops: '#2F6FED', Delivery: '#0D9488',
};

function displayName(member) {
  if (member.name) return member.name;
  if (member.email) {
    const local = member.email.split('@')[0].replace(/[._-]/g, ' ');
    return local.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (member.phone) return member.phone;
  return 'Unknown member';
}

export default function Admin() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmUsername, setConfirmUsername] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listStaff(user.idToken);
      setMembers(list);
    } catch (err) {
      setError(err.message || 'Could not load staff.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleInvite = async (data) => {
    await inviteStaff(data, user.idToken);
    setDrawerOpen(false);
    await loadStaff();
  };

  const toggleConfirm = (username) => {
    setDeleteError('');
    setConfirmUsername((current) => (current === username ? null : username));
  };

  const handleDelete = async (member) => {
    setDeleting(true);
    setDeleteError('');
    try {
      await removeStaff(member, user.idToken);
      setConfirmUsername(null);
      await loadStaff();
    } catch (err) {
      setDeleteError(err.message || 'Could not remove this staff member.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin · restricted"
        title="Staff & Roles"
        actions={<Button icon={UserPlus} onClick={() => setDrawerOpen(true)}>Invite staff</Button>}
      />

      <div className={styles.noticeBar}>
        <ShieldCheck size={15} />
        Only Admin-group members can view this page. Group changes take effect immediately.
      </div>

      {loading && <p className={styles.lastActive}>Loading…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load staff"
          description={error}
          action={<Button variant="outline" onClick={loadStaff}>Retry</Button>}
        />
      )}

      {!loading && !error && members.length === 0 && (
        <EmptyState
          icon={Users}
          title="No staff yet"
          description="Invite Ops, Admin, or Delivery accounts to see them here."
          action={<Button icon={UserPlus} onClick={() => setDrawerOpen(true)}>Invite staff</Button>}
        />
      )}

      {!loading && !error && members.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Phone</th>
                <th>Groups</th>
                <th>Status</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.username}>
                  <td>
                    <div className={styles.memberCell}>
                      <Avatar name={displayName(m)} size={34} />
                      <div>
                        <p className={styles.memberName}>{displayName(m)}</p>
                        {m.email && <p className={styles.memberEmail}>{m.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className={styles.groupTag}>{m.phone || '—'}</td>
                  <td>
                    <div className={styles.chipRow}>
                      {m.groups.map((g) => (
                        <span
                          key={g}
                          className={styles.roleTag}
                          style={{ background: `${GROUP_TINT[g]}1f`, color: GROUP_TINT[g] }}
                        >
                          {g}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={styles.lastActive}>{m.status || '—'}</td>
                  <td className={styles.actionsCell}>
                    <div className={styles.menuAnchor}>
                      <button
                        className={styles.iconBtn}
                        aria-label={`Remove ${displayName(m)}`}
                        onClick={() => toggleConfirm(m.username)}
                      >
                        <Trash2 size={15} />
                      </button>
                      {confirmUsername === m.username && (
                        <div className={styles.confirmPopover}>
                          <p className={styles.confirmTitle}>
                            <AlertTriangle size={14} /> Offboard employee
                          </p>
                          <p className={styles.confirmText}>
                            {displayName(m)} will be soft-deleted and lose all access immediately. This is not reversible from this screen.
                          </p>
                          {deleteError && <p className={styles.confirmText} style={{ color: 'var(--color-error)' }}>{deleteError}</p>}
                          <div className={styles.confirmActions}>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmUsername(null)} disabled={deleting}>
                              Cancel
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(m)} disabled={deleting}>
                              {deleting ? 'Removing…' : 'Offboard'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteStaffDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onInvite={handleInvite} />
    </div>
  );
}
